import * as Yup from "yup"

import useRouter from "@hooks/useRouter"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { AlertBanner } from "@molecules"
import { PasswordInput, PhoneNumberInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"
import { phoneNumberSchema } from "@pages/Auth/AuthPhoneCodeForm/AuthPhoneCodeForm"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import {
    AuthPhonePasswordErrorResponse,
    PhoneAndPasswordPayload,
    PhoneAndPasswordSchema,
} from "./AuthPhonePasswordForm.types"

const phoneAndPasswordSchema: Yup.ObjectSchema<PhoneAndPasswordSchema> = Yup.object()
    .concat(phoneNumberSchema)
    .shape({
        password: Yup.string().required("Please enter a password"),
    })

export default function AuthPhonePasswordForm() {
    const { preferredAuthMethod, setPhoneData, postAuthRedirectRoute } = useAuth()

    const router = useRouter()
    const { navigate } = useComponentNavigation()
    const isActive = preferredAuthMethod === "phone_and_password"

    const onMutationSuccess = () => {
        router.replace(postAuthRedirectRoute)
    }

    const onRequestCodeInsteadClick = () => {
        navigate("phone_and_code")
    }

    const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneData({
            phone: event.target.value,
            phoneId: null,
        })
    }

    return (
        <GenericForm<PhoneAndPasswordSchema, User, AuthPhonePasswordErrorResponse, PhoneAndPasswordPayload>
            formConfig={{
                defaultValues: {
                    phone_number: "",
                    password: "",
                },
            }}
            formValidationSchema={phoneAndPasswordSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.PASSWORD.SMS_AUTHENTICATE,
                genericErrorMessage: "Could not log in.",
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError }) => (
                <>
                    {nonFieldError && (
                        <AlertBanner
                            type="banner"
                            message={nonFieldError}
                            status="error"
                            className={styles.topAlertBanner}
                        />
                    )}
                    <PhoneNumberInput<PhoneAndPasswordSchema>
                        name="phone_number"
                        label="Phone number"
                        placeholder="(000) 000-0000"
                        isTabbable={isActive}
                        size="md"
                        onChangeCapture={onPhoneChange}
                    />
                    <PasswordInput<PhoneAndPasswordSchema>
                        name="password"
                        label="Password"
                        placeholder="Enter password..."
                        isTabbable={isActive}
                        size="md"
                        displayValidationMessages={false}
                    />
                    <div className={styles.formCTAs}>
                        <Button
                            type="submit"
                            size="lg"
                            variant="solid"
                            isFullWidth={true}
                            isTabbable={isActive}
                            rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                            isProcessing={isPending}
                        >
                            Sign in
                        </Button>
                        <Button
                            size="lg"
                            variant="ghost"
                            rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                            onClick={onRequestCodeInsteadClick}
                            isTabbable={isActive}
                            data-prevent-input-validation={true}
                            type="button"
                            isFullWidth={true}
                        >
                            Sign in with code
                        </Button>
                    </div>
                </>
            )}
        </GenericForm>
    )
}
